import { DashboardTierRestControllerService, type Tier } from '@api'
import { attach, createEffect, createStore, sample } from 'effector'
import { matchPath } from 'react-router'

import { Pages } from '@/consts/routes.ts'
import { $creatorId } from '@/models/creators.ts'
import { initializeApp } from '@/models/init.ts'
import { $currentPage } from '@/models/router.ts'
import { toast } from '@/models/toasts.ts'
import { handleError } from '@/utils/error.ts'

export const getTiersListFx = attach({
  name: 'get tiers list',
  source: { creatorId: $creatorId },
  mapParams: (_: void, { creatorId }) => ({
    creatorId: creatorId!,
  }),
  effect: createEffect(DashboardTierRestControllerService.listTiers),
})

export const createTierFx = attach({
  name: 'create tier',
  source: { creatorId: $creatorId },
  mapParams: (params: Omit<Tier, 'id'>, { creatorId }) => ({
    creatorId: creatorId!,
    requestBody: params,
  }),
  effect: createEffect(DashboardTierRestControllerService.createTier),
})

createTierFx.doneData.watch(() => {
  toast.success('List created successfully')
})

createTierFx.failData.watch((error) => {
  handleError(error, 'Failed to create list')
})

export const updateTierFx = attach({
  name: 'update tier',
  source: { creatorId: $creatorId },
  mapParams: ({ id, ...params }: Tier, { creatorId }) => ({
    id: id!,
    creatorId: creatorId!,
    requestBody: params,
  }),
  effect: createEffect(DashboardTierRestControllerService.editTier),
})

updateTierFx.doneData.watch(() => {
  toast.success('List updated successfully')
})

updateTierFx.failData.watch((error) => {
  handleError(error, 'Failed to update list')
})

export const removeTierFx = attach({
  name: 'remove tier',
  source: { creatorId: $creatorId },
  mapParams: (id: number, { creatorId }) => ({
    id,
    creatorId: creatorId!,
  }),
  effect: createEffect(DashboardTierRestControllerService.deleteTier),
})

removeTierFx.doneData.watch(() => {
  toast.success('Tier removed successfully')
})

removeTierFx.failData.watch((error) => {
  handleError(error, 'Error removing tier')
})

export const $tierError = createStore<Nullable<Error>>(null)
  .on(createTierFx.failData, (_, error) => error)
  .on(updateTierFx.failData, (_, error) => error)
  .reset(createTierFx, updateTierFx)

export const $tiers = createStore<Tier[]>([])
  .on(getTiersListFx.doneData, (_, tiers) => tiers)
  .on(createTierFx.doneData, (store, tier) => [...store, tier])
  .on(updateTierFx.doneData, (store, tier) =>
    store.map((item) => (item.id === tier.id ? tier : item)),
  )
  .on(removeTierFx.done, (store, { params: id }) =>
    store.filter((it) => it.id !== id),
  )
  .reset(getTiersListFx)

sample({
  clock: [initializeApp, $currentPage, $creatorId],
  source: { path: $currentPage, creatorId: $creatorId },
  filter: ({ path, creatorId }) => {
    const isPathOk =
      !!matchPath(Pages.CREATOR_SPENDING_LISTS, path) ||
      !!matchPath(Pages.CREATOR_NEW_SUBS_LIST, path)

    return !!creatorId && isPathOk
  },
  target: getTiersListFx,
})

// update data, because BE need time to create OF lists and update it in model
sample({
  clock: [createTierFx.done, updateTierFx.done],
  target: getTiersListFx,
})
